import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCircleConfig } from "@/hooks/useCircleConfig";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function CircleSettings() {
  const { config, isLoading, updateConfig } = useCircleConfig();
  const [localConfig, setLocalConfig] = useState(config);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch available tags
  const { data: tags } = useQuery({
    queryKey: ["user-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_tags")
        .select("id, name, color")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (config) {
      setLocalConfig(config);
    }
  }, [config]);

  useEffect(() => {
    setHasChanges(JSON.stringify(localConfig) !== JSON.stringify(config));
  }, [localConfig, config]);

  const handleSave = () => {
    updateConfig.mutate(localConfig);
  };

  const handleAddTag = (tagId: string) => {
    if (!localConfig.required_tags.includes(tagId)) {
      setLocalConfig({
        ...localConfig,
        required_tags: [...localConfig.required_tags, tagId],
      });
    }
  };

  const handleRemoveTag = (tagId: string) => {
    setLocalConfig({
      ...localConfig,
      required_tags: localConfig.required_tags.filter((t) => t !== tagId),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Circle Configuration</CardTitle>
          <CardDescription>
            Configure who can create Circle events and the rules around them
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Circle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable Circle</Label>
              <p className="text-sm text-muted-foreground">
                Allow users to create and join Circle events
              </p>
            </div>
            <Switch
              checked={localConfig.enabled}
              onCheckedChange={(checked) =>
                setLocalConfig({ ...localConfig, enabled: checked })
              }
            />
          </div>

          {/* Required Tags */}
          <div className="space-y-3">
            <div>
              <Label>Required Tags for Event Creation</Label>
              <p className="text-sm text-muted-foreground">
                Only users with these tags can create Circle events
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {localConfig.required_tags.map((tagId) => {
                const tag = tags?.find((t) => t.id === tagId);
                return (
                  <Badge
                    key={tagId}
                    variant="secondary"
                    className="gap-1"
                    style={tag?.color ? { backgroundColor: tag.color } : undefined}
                  >
                    {tag?.name || tagId}
                    <button
                      onClick={() => handleRemoveTag(tagId)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
            <Select onValueChange={handleAddTag}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Add a tag..." />
              </SelectTrigger>
              <SelectContent>
                {tags
                  ?.filter((t) => !localConfig.required_tags.includes(t.id))
                  .map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Credit Cost */}
          <div className="space-y-2">
            <Label htmlFor="credit-cost">Default Credit Cost</Label>
            <p className="text-sm text-muted-foreground">
              Number of credits required to join a Circle event
            </p>
            <Input
              id="credit-cost"
              type="number"
              min={0}
              value={localConfig.credit_cost}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  credit_cost: parseInt(e.target.value) || 0,
                })
              }
              className="w-24"
            />
          </div>

          {/* Cancellation Window */}
          <div className="space-y-2">
            <Label htmlFor="cancellation-window">Cancellation Window (hours)</Label>
            <p className="text-sm text-muted-foreground">
              Users can self-cancel up to this many hours before the event
            </p>
            <Input
              id="cancellation-window"
              type="number"
              min={0}
              value={localConfig.cancellation_window_hours}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  cancellation_window_hours: parseInt(e.target.value) || 24,
                })
              }
              className="w-24"
            />
          </div>

          {/* Attendance Marking Window */}
          <div className="space-y-2">
            <Label htmlFor="attendance-window">Attendance Marking Window (hours)</Label>
            <p className="text-sm text-muted-foreground">
              Community Leads can mark attendance this many hours before the event starts
            </p>
            <Input
              id="attendance-window"
              type="number"
              min={0}
              value={localConfig.attendance_window_hours}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  attendance_window_hours: parseInt(e.target.value) || 1,
                })
              }
              className="w-24"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ban Settings</CardTitle>
          <CardDescription>
            Configure automatic banning for no-shows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Auto-ban Enable */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-ban for No-shows</Label>
              <p className="text-sm text-muted-foreground">
                Automatically ban users who don't show up to events
              </p>
            </div>
            <Switch
              checked={localConfig.auto_ban_enabled}
              onCheckedChange={(checked) =>
                setLocalConfig({ ...localConfig, auto_ban_enabled: checked })
              }
            />
          </div>

          {/* Auto-ban Threshold */}
          {localConfig.auto_ban_enabled && (
            <div className="space-y-2">
              <Label htmlFor="auto-ban-threshold">No-show Threshold</Label>
              <p className="text-sm text-muted-foreground">
                Number of no-shows before automatic ban
              </p>
              <Input
                id="auto-ban-threshold"
                type="number"
                min={1}
                value={localConfig.auto_ban_threshold}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    auto_ban_threshold: parseInt(e.target.value) || 2,
                  })
                }
                className="w-24"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateConfig.isPending}
        >
          {updateConfig.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
